import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { AiContentType } from 'utils/constants/ai-enums';
const createAiDrawerSlice = (set) => ({
    visible: false,
    activeContentType: '',
    issueAnalysisTrigger: '',
    issueAnalysisIssueId: '',
    issueAnalysisForceNewConversation: false,
    issueAnalysisViewMoreType: '',
    issueAnalysisConversationId: '',
    issueAnalysisSearchConditionGroup: null,
    issueAnalysisInitialQueryText: '',
    quotedLogCrashId: '',
    quotedLogCategory: '',
    quotedLog: '',
    quotedLogMatchedLogLine: '',
    quotedLogTrigger: '',
    stackAnalysisTrigger: '',
    asmAnalysisTrigger: '',
    asmAnalysisCrashId: '',
    asmAnalysisAsmCode: '',
    chatBotTrigger: 0,
    chatBotNewConversationTrigger: 0,
    pendingIssueAnalysisId: '',
    pendingIssueAnalysisAppId: '',
    pendingCrashClawWorkspaceUid: '',
    pendingCrashClawSessionId: '',
    pendingCrashClawAutoSendText: '',
    pendingCrashClawIssueAppId: '',
    pendingCrashClawIssueId: '',
    pendingCrashClawSeq: 0,
    crashClawWorkspacesRefreshTrigger: 0,
    issueAnalysisRefreshTrigger: 0,
    issueAnalysisRefreshIssueId: '',
    chatBotFullscreen: false,
    setVisible: (v) => set((state) => ({
        ...state,
        aiDrawer: { ...state.aiDrawer, visible: v }
    }), undefined, 'aiDrawer/setVisible'),
    startIssueAnalysis: (issueId, options) => set((state) => {
        // 如果调用方传入了 trigger，使用调用方的 trigger
        // 否则生成随机 trigger（每次都创建新组件）
        const trigger = options?.trigger ?? `issue-${Date.now()}-${Math.random()}`;
        return {
            ...state,
            aiDrawer: {
                ...state.aiDrawer,
                visible: true,
                activeContentType: AiContentType.IssueAnalysis,
                issueAnalysisIssueId: issueId,
                issueAnalysisForceNewConversation: options?.forceNewConversation ?? false,
                issueAnalysisTrigger: trigger,
                issueAnalysisViewMoreType: options?.viewMoreType ?? '',
                issueAnalysisConversationId: options?.conversationId ?? '',
                issueAnalysisSearchConditionGroup: options?.searchConditionGroup ?? null,
                // 每次启动都要显式赋值（包括空串），避免复用上一次遗留的值
                issueAnalysisInitialQueryText: options?.initialQueryText ?? '',
            }
        };
    }, undefined, 'aiDrawer/startIssueAnalysis'),
    setQuotedLog: (crashId, category, log, matchedLogLine) => set((state) => ({
        ...state,
        aiDrawer: {
            ...state.aiDrawer,
            quotedLogCrashId: crashId,
            quotedLogCategory: category,
            quotedLog: log,
            quotedLogMatchedLogLine: matchedLogLine,
            quotedLogTrigger: String(Math.random()),
        }
    }), undefined, 'aiDrawer/setQuotedLog'),
    startStackAnalysis: (options) => set((state) => {
        // 如果调用方传入了 trigger，使用调用方的 trigger
        // 否则生成随机 trigger（每次都创建新组件）
        const trigger = options?.trigger ?? `stack-${Date.now()}-${Math.random()}`;
        return {
            ...state,
            aiDrawer: {
                ...state.aiDrawer,
                visible: true,
                activeContentType: AiContentType.StackAnalysis,
                stackAnalysisTrigger: trigger,
            }
        };
    }, undefined, 'aiDrawer/startStackAnalysis'),
    startAsmAnalysis: (crashId, asmCode, options) => set((state) => {
        // 如果调用方传入了 trigger，使用调用方的 trigger
        // 否则生成随机 trigger（每次都创建新组件）
        const trigger = options?.trigger ?? `asm-${Date.now()}-${Math.random()}`;
        return {
            ...state,
            aiDrawer: {
                ...state.aiDrawer,
                visible: true,
                activeContentType: AiContentType.AsmAnalysis,
                asmAnalysisCrashId: crashId,
                asmAnalysisAsmCode: asmCode,
                asmAnalysisTrigger: trigger,
            }
        };
    }, undefined, 'aiDrawer/startAsmAnalysis'),
    startChatBot: () => set((state) => ({
        ...state,
        aiDrawer: {
            ...state.aiDrawer,
            visible: true,
            activeContentType: AiContentType.ChatBot,
            chatBotTrigger: state.aiDrawer.chatBotTrigger + 1,
        }
    }), undefined, 'aiDrawer/startChatBot'),
    startChatBotWithIssue: (issueId, appId) => set((state) => ({
        ...state,
        aiDrawer: {
            ...state.aiDrawer,
            visible: true,
            activeContentType: AiContentType.ChatBot,
            chatBotTrigger: state.aiDrawer.chatBotTrigger + 1,
            pendingIssueAnalysisId: issueId,
            pendingIssueAnalysisAppId: appId,
            pendingCrashClawWorkspaceUid: '',
            pendingCrashClawSessionId: '',
            pendingCrashClawAutoSendText: '',
            pendingCrashClawIssueAppId: '',
            pendingCrashClawIssueId: '',
        }
    }), undefined, 'aiDrawer/startChatBotWithIssue'),
    startChatBotWithCrashClaw: (opts) => set((state) => ({
        ...state,
        aiDrawer: {
            ...state.aiDrawer,
            visible: true,
            activeContentType: AiContentType.ChatBot,
            chatBotTrigger: state.aiDrawer.chatBotTrigger + 1,
            pendingIssueAnalysisId: '',
            pendingIssueAnalysisAppId: '',
            pendingCrashClawWorkspaceUid: opts.workspaceUid || '',
            pendingCrashClawSessionId: opts.sessionId || '',
            pendingCrashClawAutoSendText: opts.autoSendText || '',
            pendingCrashClawIssueAppId: opts.appId || '',
            pendingCrashClawIssueId: opts.issueId || '',
            pendingCrashClawSeq: state.aiDrawer.pendingCrashClawSeq + 1,
        }
    }), undefined, 'aiDrawer/startChatBotWithCrashClaw'),
    startChatBotNewConversation: () => set((state) => ({
        ...state,
        aiDrawer: {
            ...state.aiDrawer,
            chatBotNewConversationTrigger: state.aiDrawer.chatBotNewConversationTrigger + 1,
        }
    }), undefined, 'aiDrawer/startChatBotNewConversation'),
    refreshCrashClawWorkspaces: () => set((state) => ({
        ...state,
        aiDrawer: {
            ...state.aiDrawer,
            crashClawWorkspacesRefreshTrigger: state.aiDrawer.crashClawWorkspacesRefreshTrigger + 1,
        }
    }), undefined, 'aiDrawer/refreshCrashClawWorkspaces'),
    bumpIssueAnalysisRefresh: (issueId) => set((state) => ({
        ...state,
        aiDrawer: {
            ...state.aiDrawer,
            issueAnalysisRefreshTrigger: state.aiDrawer.issueAnalysisRefreshTrigger + 1,
            issueAnalysisRefreshIssueId: issueId || '',
        }
    }), undefined, 'aiDrawer/bumpIssueAnalysisRefresh'),
    setChatBotFullscreen: (fullscreen) => set((state) => ({
        ...state,
        aiDrawer: {
            ...state.aiDrawer,
            chatBotFullscreen: fullscreen,
        }
    }), undefined, 'aiDrawer/setChatBotFullscreen'),
});
const createNoticeDrawerSlice = (set) => ({
    unreadAlarmPushFailedCount: 0,
    openPushFailedTrigger: 0,
    setUnreadAlarmPushFailedCount: (count) => set((state) => ({
        ...state,
        noticeDrawer: {
            ...state.noticeDrawer,
            unreadAlarmPushFailedCount: count,
        },
    }), undefined, 'noticeDrawer/setUnreadAlarmPushFailedCount'),
    openNoticeDrawerForPushFailed: () => set((state) => ({
        ...state,
        noticeDrawer: {
            ...state.noticeDrawer,
            openPushFailedTrigger: state.noticeDrawer.openPushFailedTrigger + 1,
        },
    }), undefined, 'noticeDrawer/openNoticeDrawerForPushFailed'),
});
const useZuStore = create()(devtools(((set, get, api) => ({
    aiDrawer: createAiDrawerSlice(set, get, api),
    noticeDrawer: createNoticeDrawerSlice(set, get, api),
})), {
    name: 'ZuStore',
    enabled: process.env.NODE_ENV !== 'production',
}));
export default useZuStore;
